import React, { useState, useEffect, useCallback, useRef } from "react";
import AsnList from "./AsnList";
import CreateASNTabs from "./CreateASNTabs";
import { useDispatch } from "react-redux";
import { setLoadTable } from "../../redux/action/skuTable.action";
import { fetchASN, fetchAsnById, fetchAsnByAsnNumber } from "../../services/asnAPI/asn";
import { toast } from "../../components/AppToast";
import { formatToInputDate } from "../../utils/dateFormatter";
import GoodsReceipt from "../Grn/GoodsReceipt"; // 👈 NEW inline GRN panel (no router)
import ViewAppointment from "../appointment/ViewAppointment";
import { fetchAppointmentById } from "../../services/appointment/appointment";




const isEmptyObject = (obj) =>
  !obj ||
  (typeof obj === "object" &&
    !Array.isArray(obj) &&
    Object.keys(obj).length === 0);

// 1 = SKU, 2 = PO, 3 = Blind. Blind lines have no purchase order, so they
// must not fall through into a PO::unknown group.
function lineItemGroupKey(createUsing, item) {
  const mode = Number(createUsing);
  if (mode === 1) return `SKU::${item?.skuId || "unknown"}`;
  if (mode === 3) return "BLIND";
  return `PO::${item?.purchaseOrder || "unknown"}`;
}

function mapAsnLineItemRow(item, idx) {
  const orderedQty = Number(item?.orderedQty ?? item?.orderQty ?? 0) || 0;
  const lineNumber = item?.lineNumber || String(idx + 1).padStart(4, "0");
  return {
    lineNo: lineNumber,
    lineNumber,
    item: item?.skuId || "",
    skuId: item?.skuId || "",
    purchaseOrder: item?.purchaseOrder || "",
    city: item?.poDetails?.[0]?.buyer?.address?.city || "",
    state: item?.poDetails?.[0]?.buyer?.address?.state || "",
    description: item?.description || "",
    pack: item?.pack || "STD",
    packKey: item?.packKey || "",
    lpnNo: item?.lpnNo || item?.lpn || "",
    batchNum: item?.batchNum || item?.batchNo || item?.batchNumber || "",
    manufacturingDate: item?.manufacturingDate || "",
    expirationDate: item?.expirationDate || "",
    uom: item?.uom || "EA",
    itemWeight: item?.itemWeight || 0,
    itemVolume: item?.itemVolume || 0,
    itemweight: item?.itemWeight ?? item?.itemweight ?? "",
    itemcode: item?.itemVolume ?? item?.itemcode ?? "",
    receivedQty: item?.receivedQty || 0,
    shortQty: item?.shortQty || 0,
    damagedQty: item?.damagedQty || 0,
    excessQty: item?.excessQty || 0,
    expectedQty: orderedQty,
    orderQty: orderedQty,
    orderedQty,
    openQty: item?.openQty || 0,
    dispatchQty: item?.dispatchQty || 0,
    lineItemId: item?.lineItemId || null,
    asnDiscount: item?.asnDiscount || 0,
    asnUnitPrice: item?.asnUnitPrice || 0,
    asnTotalPrice: item?.asnTotalPrice || 0,
    discount: item?.discount || 0,
    tax: item?.tax || 0,
    unitPrice: item?.unitPrice || 0,
    poTotalPrice: item?.poTotalPrice || 0,
    itemPOTolerance: item?.itemPOTolerance || 0,
  };
}

function buildAsnLineItemTable(selectedAsn) {
  const tableData = {};
  const createUsing = selectedAsn?.asnDetails?.createUsing;
  (selectedAsn?.lineItems || []).forEach((item, idx) => {
    const key = lineItemGroupKey(createUsing, item);
    if (!tableData[key]) tableData[key] = [];
    tableData[key].push(mapAsnLineItemRow(item, idx));
  });
  return tableData;
}

// single-ASN fallback fetcher (use your existing search endpoint you used earlier)
async function fetchAsnByNumber(asnNumber) {
  if (!asnNumber) return null;

  // 1) Try your search endpoint (you already used similar in update code)
  try {
    const res = await fetch(`/api/asn/search?asnNumber=${encodeURIComponent(asnNumber)}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    const json = await res.json();
    const rec =
      (Array.isArray(json) && json[0]) ||
      (Array.isArray(json?.data) && json.data[0]) ||
      json?.data ||
      json?.item ||
      json;

    if (rec) return normalizeAsnRecord(rec);
  } catch (e) {
    console.warn("fetchAsnByNumber(search) failed", e);
  }

  // 2) Fallback: pull full list and filter
  try {
    const full = await fetchASN();
    const found = (full || []).find(a =>
      (a?.asnDetails?.asnNumber || a?.asnNumber || a?._id) === asnNumber
    );
    return found ? normalizeAsnRecord(found) : null;
  } catch (e) {
    console.warn("fetchAsnByNumber(list) failed", e);
    return null;
  }
}

// Normalize various API shapes into the one your viewers expect
function normalizeAsnRecord(input) {
  // If already in correct shape, return as-is
  if (input?.asnDetails) return input;

  // If flat shape (asnNumber & lineItems etc. on root), wrap into expected keys
  const asnNumber = input?.asnNumber || input?.asnNo || input?.id || input?._id || "";
  return {
    _id: input?._id || input?.id,
    asnDetails: {
      ...(input?.asnDetails || {}),
      asnNumber,
      createUsing: input?.createUsing ?? input?.asnDetails?.createUsing ?? 1,
      receiptStatus: input?.receiptStatus ?? input?.asnDetails?.receiptStatus ?? "New",
      closedDate: input?.closedDate || input?.asnDetails?.closedDate || null,
    },
    shipFrom: input?.shipFrom || {},
    shipTo: input?.shipTo || {},
    carrier: input?.carrier || {},
    load: input?.load || {},
    invoice: input?.invoice || {},
    userDefined: input?.userDefined || {},
    lineItems: input?.lineItems || input?.items || [],
    createdAt: input?.createdAt || null,
    status: input?.status,
  };
}


const ASNManagement = ({ initialValues, onCancel, onSubmit, initialAsnNumber, onDeepLinkNotFound, initialAsnId }) => {
  const [isCreating, setIsCreating] = useState(false);
  const [activeAsn, setActiveAsn] = useState(null);
  const [listVersion, setListVersion] = useState(0);
  const [asnData, setAsnData] = useState([]);
  const [, setLoading] = useState(false);
  const dispatch = useDispatch();

  // 🔎 deep-link: also support GRN (like PO→ASN)
  const [deepView, setDeepView] = useState(null); // { type:'GRN', id:'GRN123' }

  const goBackAfterNotFound = useCallback(() => {
    if (typeof onDeepLinkNotFound === "function") {
      onDeepLinkNotFound();
    } else if (typeof onCancel === "function") {
      onCancel(); // fallback: parent ka onCancel (PO pe wapas)
    }
  }, [onDeepLinkNotFound, onCancel]);
  console.log("initialAsnNumber:>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>", initialAsnNumber);

  // 🔗 NEW: deep-link by ASN ID (when coming from PO)
  // 🔗 Handle ASN deep-link using ASN NUMBER (via fetchAsnByAsnNumber)
  useEffect(() => {
    const wanted = String(initialAsnNumber || "").trim();
    if (!wanted) return;                          // No ASN number provided
    if (deepLinkDoneRef.current) return;          // Prevent duplicate runs

    // (Optional) If an ASN ID is also provided, the ID-based effect will handle it
    // if (initialAsnId) return;

    (async () => {
      try {
        setLoading(true);

        // ✅ Fetch ASN by its number (new API)
        const found = await fetchAsnByAsnNumber(wanted);

        // ❌ If API returns blank or empty object
        if (!found || isEmptyObject(found)) {
          throw new Error(`ASN ${wanted} not found`);
        }

        // Extract ASN number from the response
        const asnNumber =
          found?.asnDetails?.asnNumber ||
          found?.asnNumber ||
          found?._id ||
          "";

        if (!asnNumber) {
          throw new Error("ASN number missing from API response");
        }

        // ✅ Open ASN in View Mode
        continueWithViewAsn(found, asnNumber);
        deepLinkDoneRef.current = true;
      } catch (err) {
        console.error("Deep-link by ASN number failed:", err);

        // Prepare user-friendly error message
        const msg =
          typeof err === "string"
            ? err
            : err?.message || `Failed to load ASN ${wanted}`;

        toast.error(msg);

        // ⬅️ Go back to parent screen (PO/GRN)
        goBackAfterNotFound();
      } finally {
        setLoading(false);
      }
    })();
  }, [initialAsnNumber, goBackAfterNotFound]);



  // one-time guards for deep-link flow (ASN)
  const deepLinkTriedFreshRef = useRef(false);
  const deepLinkNotifiedRef = useRef(false);
  const deepLinkDoneRef = useRef(false);

  // const goBackAfterNotFound = useCallback(() => {
  //   if (typeof onDeepLinkNotFound === "function") onDeepLinkNotFound();
  // }, [onDeepLinkNotFound]);






  // 🔗 Handle ASN deep-link (unchanged logic)
  useEffect(() => {
    const wanted = String(initialAsnNumber || "").trim();
    if (!wanted) return;
    if (deepLinkDoneRef.current) return;
    if (!asnData || asnData.length === 0) return;

    const inList = asnData.find(a => a?.asnDetails?.asnNumber === wanted);
    if (inList) {
      continueWithViewAsn(inList, wanted);
      deepLinkDoneRef.current = true;
      return;
    }

    // not in list → one-time fresh fetch
    if (!deepLinkTriedFreshRef.current) {
      deepLinkTriedFreshRef.current = true;
      (async () => {
        try {
          setLoading(true);
          const fresh = await fetchASN(`?t=${Date.now()}`);
          setAsnData(fresh || []);
          const found = (fresh || []).find(a => a?.asnDetails?.asnNumber === wanted);
          if (found) {
            continueWithViewAsn(found, wanted);
            deepLinkDoneRef.current = true;
          } else if (!deepLinkNotifiedRef.current) {
            deepLinkNotifiedRef.current = true;
            toast.error(`ASN ${wanted} not found`);
            goBackAfterNotFound();
          }
        } catch {
          if (!deepLinkNotifiedRef.current) {
            deepLinkNotifiedRef.current = true;
            toast.error(`Failed to load ASN ${wanted}`);
            goBackAfterNotFound();
          }
        } finally {
          setLoading(false);
        }
      })();
    } else if (!deepLinkNotifiedRef.current) {
      deepLinkNotifiedRef.current = true;
      toast.error(`ASN ${wanted} not found`);
      goBackAfterNotFound();
    }
  }, [initialAsnNumber, asnData, goBackAfterNotFound]);

  const wantedAsn = String(initialAsnNumber || "").trim();
  const isDeepLinkedView =
    !!wantedAsn && !!activeAsn && activeAsn.mode === "view" &&
    String(activeAsn.asnNumber || "") === wantedAsn;
  const injectedFromOutside = Boolean(initialAsnNumber);

  // View ASN (same as your previous)
  const continueWithViewAsn = (selectedAsn, asnNumber) => {
    try {
      const asnDetails = selectedAsn.asnDetails || {};
      const shipFrom = selectedAsn.shipFrom || {};
      const shipTo = selectedAsn.shipTo || {};
      const carrier = selectedAsn.carrier || {};
      const load = selectedAsn.load || {};
      const invoice = selectedAsn.invoice || {};
      const userDefined = selectedAsn.userDefined || {};

      const formatDateField = (v) => (v ? formatToInputDate(v, false) : '');
      const formatDateTimeField = (v) => (v ? formatToInputDate(v, true) : '');

      dispatch(setLoadTable(buildAsnLineItemTable(selectedAsn)));

      const formData = {
        ...asnDetails,
        ...shipFrom,
        ...shipTo,
        ...carrier,
        ...load,
        ...invoice,
        ...userDefined,
        // header Owner is the partner_id; shipTo.owner must not override it
        owner: asnDetails.owner ?? shipTo.owner ?? '',
        buyerName: shipTo.buyerName ?? shipTo.owner ?? '',
        _id: selectedAsn._id,
        actualDepartureDate: formatDateField(load.actualDepartureDate),
        expectedArrivalDate: formatDateField(load.expectedArrivalDate),
        actualArrivalDate: formatDateField(load.actualArrivalDate),
        invoiceDate: formatDateField(invoice.invoiceDate),
        closedDate: formatDateField(asnDetails.closedDate),
        asnCreatedate: formatDateTimeField(selectedAsn.createdAt),
        asnNumber: asnDetails.asnNumber || asnNumber,
        __viewOnly: true
      };

      setActiveAsn({ mode: 'view', asnNumber, initialValues: formData });
      setIsCreating(false);
    } catch (err) {
      toast.error(`Failed to process ASN: ${err?.message || err}`);
    }
  };

  // Close form and refresh
  const closeForm = () => {
    setActiveAsn(null);
    setIsCreating(false);
    dispatch(setLoadTable({}));
    setListVersion(v => v + 1);
  };

  // 🔗 GRN link clicked in ASN list → open GRN panel here (no router)
  const handleOpenGrn = (grnNo) => {
    console.log("handleOpenGrn called with:", grnNo);
    if (!grnNo) return;
    setDeepView({ type: 'GRN', id: String(grnNo.grnNumber) });
  };

  // 🔗 Appointment Key in ASN list → inline View Appointment (same idea as GRN)
  const handleOpenAppointment = async (appointmentKey) => {
    const key = String(appointmentKey || "").trim();
    if (!key) return;
    try {
      setLoading(true);
      const apiData = await fetchAppointmentById(key);
      if (!apiData) {
        toast.error("Appointment not found");
        return;
      }
      setDeepView({ type: "APPOINTMENT", id: key, data: apiData });
    } catch (err) {
      toast.error(
        typeof err === "string" ? err : err?.message || "Failed to load appointment"
      );
    } finally {
      setLoading(false);
    }
  };

  const exitDeepView = () => setDeepView(null);

  const onlyFormVisible = isCreating || !!activeAsn || !!deepView;


  // helper to open an ASN record in a particular mode
  const openAsnWithMode = (selectedAsn, asnNumber, mode) => {
    try {
      const asnDetails = selectedAsn.asnDetails || {};
      const shipFrom = selectedAsn.shipFrom || {};
      const shipTo = selectedAsn.shipTo || {};
      const carrier = selectedAsn.carrier || {};
      const load = selectedAsn.load || {};
      const invoice = selectedAsn.invoice || {};
      const userDefined = selectedAsn.userDefined || {};

      const formatDateField = (v) => (v ? formatToInputDate(v, false) : '');
      const formatDateTimeField = (v) => (v ? formatToInputDate(v, true) : '');

      dispatch(setLoadTable(buildAsnLineItemTable(selectedAsn)));

      const formData = {
        ...asnDetails,
        ...shipFrom,
        ...shipTo,
        ...carrier,
        ...load,
        ...invoice,
        ...userDefined,
        // header Owner is the partner_id; shipTo.owner must not override it
        owner: asnDetails.owner ?? shipTo.owner ?? '',
        buyerName: shipTo.buyerName ?? shipTo.owner ?? '',
        _id: selectedAsn._id,
        actualDepartureDate: formatDateField(load.actualDepartureDate),
        expectedArrivalDate: formatDateField(load.expectedArrivalDate),
        actualArrivalDate: formatDateField(load.actualArrivalDate),
        invoiceDate: formatDateField(invoice.invoiceDate),
        closedDate: formatDateField(asnDetails.closedDate),
        asnCreatedate: formatDateTimeField(selectedAsn.createdAt),
        asnNumber: asnDetails.asnNumber || asnNumber,
      };

      setActiveAsn({ mode, asnNumber, initialValues: formData });
      setIsCreating(false);
    } catch (err) {
      toast.error(`Failed to open ASN: ${err?.message || err}`);
    }
  };

  // called by AsnList -> Edit
  // called by AsnList -> Edit
  const openAsnForEditOLD = async (row) => {
    try {
      const getById = await fetchAsnById(row._id);
      const asnNumber = getById?.asnDetails?.asnNumber;
      let found = getById;
      console.log("getById", getById);

      if (!found) found = await fetchAsnByNumber(asnNumber);

      if (found) {
        // Use openAsnWithMode to open in edit mode (not view)
        openAsnWithMode(found, asnNumber, "edit");
      } else {
        toast.error(`ASN ${asnNumber} not found`);
      }
    } catch (err) {
      console.error("openAsnForEdit error:", err);
      toast.error("Failed to open ASN for edit");
    }
  };



  // called by AsnList -> Add Details
  const openAsnForAddDetailsOLD = async (row) => {
    try {
      const getById = await fetchAsnById(row._id);
      const asnNumber = getById?.asnDetails?.asnNumber;
      let found = getById;
      console.log("getById", getById);

      if (!found) found = await fetchAsnByNumber(asnNumber);

      if (found) {
        // Use openAsnWithMode to open in addDetails mode
        openAsnWithMode(found, asnNumber, "addDetails");
      } else {
        toast.error(`ASN ${asnNumber} not found`);
      }
    } catch (err) {
      console.error("openAsnForAddDetails error:", err);
      toast.error("Failed to open ASN for add details");
    }
  };


  const openAsnForEdit = async (row) => {
    try {
      const getById = await fetchAsnById(row._id);
      console.log("getById", getById);

      if (!getById || isEmptyObject(getById)) {
        throw new Error("Empty ASN response");
      }

      const asnNumber =
        getById?.asnDetails?.asnNumber ||
        getById?.asnNumber ||
        getById?._id;

      if (!asnNumber) {
        throw new Error("ASN number missing");
      }

      let found = getById;
      if (!found) found = await fetchAsnByNumber(asnNumber);

      if (found) {
        openAsnWithMode(found, asnNumber, "edit");
      } else {
        toast.error(`ASN ${asnNumber} not found`);
      }
    } catch (err) {
      console.error("openAsnForEdit error:", err);
      toast.error(err?.message || "Failed to open ASN for edit");
    }
  };



  const openAsnForAddDetails = async (row) => {
    try {
      const getById = await fetchAsnById(row._id);
      console.log("getById", getById);

      if (!getById || isEmptyObject(getById)) {
        throw new Error("Empty ASN response");
      }

      const asnNumber =
        getById?.asnDetails?.asnNumber ||
        getById?.asnNumber ||
        getById?._id;

      if (!asnNumber) {
        throw new Error("ASN number missing");
      }

      let found = getById;
      if (!found) found = await fetchAsnByNumber(asnNumber);

      if (found) {
        openAsnWithMode(found, asnNumber, "addDetails");
      } else {
        toast.error(`ASN ${asnNumber} not found`);
      }
    } catch (err) {
      console.error("openAsnForAddDetails error:", err);
      toast.error(err?.message || "Failed to open ASN for add details");
    }
  };



  return (
    <div className="card shadow-lg main-card" style={{ position: 'relative' }}>
      {/* Header */}
      {!isDeepLinkedView && (
        <div className="card-header bg-secondary text-white d-flex justify-content-between align-items-center">
          <h4 className="mb-0 h6 fw-semibold">
            {deepView?.type === 'APPOINTMENT' ? `View Appointment` :
              deepView?.type === 'GRN' ? `Goods Receipt` :
              activeAsn ? (
                activeAsn.mode === 'view' ? 'View ASN' :
                  activeAsn.mode === 'addDetails' ? 'Add Details' :
                    'Edit ASN'
              ) : isCreating ? 'Create ASN' : 'ASN Details'}
          </h4>
        </div>
      )}

      {/* Breadcrumbs (hide when deep-linked inside PO) */}
      {(isCreating || activeAsn || deepView) && !isDeepLinkedView && !injectedFromOutside && (
        <nav aria-label="breadcrumb" className="px-3 py-2 border-bottom bg-light mb-2">
          <ol className="breadcrumb mb-0 align-items-center">
            <li className="breadcrumb-item">
              <button
                type="button"
                className="btn btn-link p-0 text-decoration-none text-muted"
                onClick={() => {
                  setIsCreating(false);
                  setActiveAsn(null);
                  setDeepView(null);
                  dispatch(setLoadTable({}));
                  setListVersion(v => v + 1);
                }}
              >
                ASN
              </button>
            </li>
            <li className="breadcrumb-item active fw-semibold text-primary" aria-current="page">
              {deepView?.type === 'APPOINTMENT'
                ? `Appointment ${deepView.id}`
                : deepView?.type === 'GRN'
                ? `GRN ${deepView.id}`
                : isCreating && !activeAsn ? 'Create ASN'
                  : activeAsn?.mode === 'edit' ? 'Edit ASN'
                    : activeAsn?.mode === 'addDetails' ? 'Add Details'
                      : activeAsn?.mode === 'view' ? 'View ASN'
                        : 'ASN Details'}
            </li>
          </ol>
        </nav>
      )}

      <div className="card-body">
        {/* Conditional views */}
        {deepView?.type === "APPOINTMENT" ? (
          <ViewAppointment
            initialValues={deepView.data || {}}
            onCancel={exitDeepView}
          />
        ) : deepView?.type === 'GRN' ? (
          <GoodsReceipt
            initialGrnNumber={String(deepView.id)}
            onBack={exitDeepView}
            
          />
        ) : isCreating ? (
          <CreateASNTabs
            initialValues={initialValues}
            onCancel={closeForm}
            onSubmit={(data) => { onSubmit?.(data); closeForm(); }}
            isEdit={false}
            viewOnly={false}
          />
        ) : activeAsn ? (
          <CreateASNTabs
            initialValues={activeAsn.initialValues}
            onCancel={closeForm}
            onSubmit={(data) => { onSubmit?.(data); closeForm(); }}
            isEdit={activeAsn.mode === 'edit' || activeAsn.mode === 'addDetails'}
            viewOnly={activeAsn.mode === 'view'}
            isAddDetailsMode={activeAsn.mode === 'addDetails'}
          />
        ) : (
          <AsnList
                  onViewAsn={async (row) => {
                    try {
                      const getById = await fetchAsnById(row._id);
                      console.log("getById", getById);

                      if (!getById || isEmptyObject(getById)) {
                        throw new Error("Empty ASN response");
                      }

                      const asnNumber =
                        getById?.asnDetails?.asnNumber ||
                        getById?.asnNumber ||
                        getById?._id;

                      if (!asnNumber) {
                        throw new Error("ASN number missing");
                      }

                      let found = getById;
                      if (!found) found = await fetchAsnByNumber(asnNumber);

                      if (found) {
                        continueWithViewAsn(found, asnNumber);
                      } else {
                        toast.error(`ASN ${asnNumber} not found`);
                      }
                    } catch (err) {
                      console.error("onViewAsn error:", err);
                      toast.error(err?.message || "Failed to open ASN");
                    }
                  }}
            onEditAsn={openAsnForEdit}               // <- wired
            onAddDetailsAsn={openAsnForAddDetails}   // <- wired
            onOpenGrn={handleOpenGrn}
            onOpenAppointment={handleOpenAppointment}
            refreshKey={listVersion}
            showCreateButton={true}
            createButtonText="Create New ASN"
            createButtonIcon="bi-plus-circle"
            onCreateClick={() => { setIsCreating(true); dispatch(setLoadTable({})); }}
            onhyperlinkOpen={initialAsnId}
          />

        )}
      </div>
    </div>
  );
};

export default ASNManagement;
